import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  EstablishmentOpenedStatus,
  TemporaryClosure,
  WorkingDays,
  getEstablishmentOpenedStatus,
  getDateReferenceForTimezone,
  isEstablishmentAboutToClose,
  isEstablishmentOpened,
  isEstablishmentTemporaryClosed,
} from '@pedix-workspace/utils';
import { Observable, of, timer } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class WorkingHoursService {
  private platformId = inject(PLATFORM_ID);

  isEstablishmentOpened$(
    workingDays: WorkingDays | undefined,
    temporaryClosure: TemporaryClosure | undefined,
    options: { timezone?: string; isInventoryBlocked?: boolean },
  ): Observable<EstablishmentOpenedStatus> {
    if (isPlatformServer(this.platformId)) {
      return of(this.computeOpenedStatus(workingDays, temporaryClosure, options));
    }

    const secondsToMinuteOClock = 60 - new Date().getSeconds();

    return timer(secondsToMinuteOClock * 1000, 60000).pipe(
      map(() => this.computeOpenedStatus(workingDays, temporaryClosure, options)),
      startWith(this.computeOpenedStatus(workingDays, temporaryClosure, options)),
    );
  }

  private computeOpenedStatus(
    workingDays: WorkingDays | undefined,
    temporaryClosure: TemporaryClosure | undefined,
    options: { timezone?: string; isInventoryBlocked?: boolean },
  ): EstablishmentOpenedStatus {
    const dateReference = options.timezone
      ? getDateReferenceForTimezone(options.timezone)
      : undefined;

    return getEstablishmentOpenedStatus(
      {
        isOpened: isEstablishmentOpened(workingDays, dateReference),
        isAboutToClose: isEstablishmentAboutToClose(workingDays, { dateReference }),
        isTemporaryClosed: isEstablishmentTemporaryClosed(temporaryClosure, { dateReference }),
      },
      workingDays,
      options.isInventoryBlocked ?? false,
    );
  }
}
